import {
  ApiDashboardPortfolioStackedBarChartDataRetrieveIndicatorsEnum,
  ApiDashboardPortfolioStackedBarChartDataRetrieveRequest,
  DashboardStackedMonthlyVar,
} from '@api';
import { CircleLoader } from '@components/ui';
import { useApi } from '@data-access';
import { chartColors, MapperHelper } from '@helpers';
import { notionStore } from '@stores/notion.store';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  ChartOptions,
  Legend,
} from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { SelectedFiltersType } from 'src/pages/dashboard';

import { ChartCard } from '../ChartCard/ChartCard';

import styles from './RiskIndicators.module.scss';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

function label(context: any) {
  return `${context.dataset.label}: ${MapperHelper.roundNumber(context.parsed.y * 100, 2)}%`;
}

const options: ChartOptions<'bar'> = {
  plugins: {
    datalabels: {
      anchor: 'end',
      align: 'top',
      formatter(_, context: Context) {
        let total = 0; // sum of all visible datasets
        const index = context.dataIndex;
        context.chart.data.datasets.forEach((d: any, idx: number) => {
          total += context.chart.isDatasetVisible(idx) ? d.data[index] : 0;
        });

        let lastVisibleDatasetIndex = 0;

        context?.chart?.data?.datasets.forEach((value, idx) => {
          if (context.chart.isDatasetVisible(idx)) lastVisibleDatasetIndex = idx; // get index of last visible dataset
        });

        if (context.datasetIndex === lastVisibleDatasetIndex) return `${MapperHelper.roundNumber(total * 100, 2)}%`;
        return '';
      },
    },
    title: {
      display: false,
    },
    legend: {
      position: 'bottom' as any,
      labels: {
        padding: 16,
        pointStyle: 'rectRounded',
        usePointStyle: true,
      },
    },
    tooltip: {
      padding: 6,
      backgroundColor: '#3d5a80d4',
      callbacks: {
        label,
      },
    },
  },
  devicePixelRatio: 2.5,
  datasets: {
    bar: {
      barThickness: 18,
      borderRadius: { topLeft: 10, topRight: 10, bottomLeft: 10, bottomRight: 10 },
      borderSkipped: false,
      borderWidth: 2,
      borderColor: 'transparent',
    },
  },
  layout: { padding: { top: 20 } },
  scales: {
    x: {
      stacked: true,
      grid: {
        display: false,
      },
      border: {
        color: '#e5e7eb',
        width: 1,
      },
      ticks: {
        color: '#4b5052',
      },
    },
    y: {
      stacked: true,
      grid: {
        color: '#e5e7eb',
      },
      border: {
        color: '#e5e7eb',
        width: 2,
        dash: [8, 4],
      },
      ticks: {
        font: {
          weight: 500,
        },
        callback(value: any) {
          return `${MapperHelper.roundNumber(value * 100, 2)}%`;
        },
      },
    },
  },
  responsive: true,
  maintainAspectRatio: false,
};

interface DatasetType {
  label: string;
  data: number[];
  backgroundColor: string;
  hoverBackgroundColor: string;
}

interface RiskIndicatorsProbProps {
  appliedFilters: SelectedFiltersType;
  fixedWidth?: number;
}

export const RiskIndicatorsProb: React.FC<RiskIndicatorsProbProps> = observer(({ appliedFilters, fixedWidth }) => {
  const { dashboardApi } = useApi();
  const { values } = notionStore;
  const [riskIndicators, setRiskIndicators] = useState<DashboardStackedMonthlyVar[]>();

  const riskIndicatorsData = useMemo(() => {
    const datasets: DatasetType[] = [
      {
        label: 'Performance',
        data: [],
        backgroundColor: chartColors.performance.backgroundColor,
        hoverBackgroundColor: chartColors.performance.hoverBackgroundColor,
      },
      {
        label: 'Robustness',
        data: [],
        backgroundColor: chartColors.robustness.backgroundColor,
        hoverBackgroundColor: chartColors.robustness.hoverBackgroundColor,
      },
      {
        label: 'Fairness',
        data: [],
        backgroundColor: chartColors.fairness.backgroundColor,
        hoverBackgroundColor: chartColors.fairness.hoverBackgroundColor,
      },
      {
        label: 'Explainability',
        data: [],
        backgroundColor: chartColors.explainability.backgroundColor,
        hoverBackgroundColor: chartColors.explainability.hoverBackgroundColor,
      },
    ];
    const months: string[] = [];

    riskIndicators?.forEach(indicator => {
      datasets[0].data.push(indicator.performanceValue || 0);
      datasets[1].data.push(indicator.robustnessValue || 0);
      datasets[2].data.push(indicator.fairnessValue || 0);
      datasets[3].data.push(indicator.explainabilityValue || 0);
      months.push(dayjs(indicator.month).format('MMMM YYYY'));
    });
    return { labels: months, datasets };
  }, [riskIndicators]);

  const fetchStackedChartData = useCallback(
    async (params?: ApiDashboardPortfolioStackedBarChartDataRetrieveRequest) => {
      try {
        const { results } = await dashboardApi.apiDashboardPortfolioStackedBarChartDataRetrieve(params);
        setRiskIndicators(results);
      } catch (error) {
        console.log(error);
      }
    },
    [dashboardApi],
  );

  useEffect(() => {
    fetchStackedChartData({
      aiModels: appliedFilters.models?.length > 0 ? appliedFilters.models : undefined,
      date: appliedFilters.untilDate ? dayjs(appliedFilters.untilDate).format('YYYY-MM').toString() : undefined,
      departments: appliedFilters.departments?.length > 0 ? appliedFilters.departments : undefined,
      useCases: appliedFilters.useCases?.length > 0 ? appliedFilters.useCases : undefined,
      limit: 12,
      indicators: Object.values(ApiDashboardPortfolioStackedBarChartDataRetrieveIndicatorsEnum),
    });
  }, [
    appliedFilters.departments,
    appliedFilters.models,
    appliedFilters.untilDate,
    appliedFilters.useCases,
    fetchStackedChartData,
  ]);

  return (
    <ChartCard
      className={styles.card}
      title="Risk Progression"
      subtitle="Progression of risk exposure per risk indicator in the filtered portfolio"
      infoPosition="top"
      info={values?.riskIndicators || ' '}
    >
      {!riskIndicators ? (
        <div className={styles.loaderWrapper}>
          <CircleLoader />
        </div>
      ) : (
        <div style={{ width: `${fixedWidth}%` }}>
          <Bar
            options={options}
            data={riskIndicatorsData}
            height={500}
          />
        </div>
      )}
    </ChartCard>
  );
});
